import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { ArcnList } from "@feature/types/arcnlist.type"
import {  ArInvoiceEntity } from '@feature/types/arinvoiceentity';
import { WOSChargeLineEntity } from '@feature/types/woschargelineentity';
import { GetTaxCodeRequest, GetTaxCodeResponse } from '@feature/types/gettaxcoderequest.type';



import { ENV } from "@app/constants/env";
import { getToken } from '@app/helpers/authProvider';
import { arcncopychargeline, ARInvoiceSummary } from '@feature/types/arinvoicesummery.type';
import { ArCreditNote } from '@feature/types/arcreditnote.type';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL +ENV.FINANCE_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken(); // ensure this returns a non-empty string
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set('Authorization', `Bearer ${token}`); // use standard casing
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    // optional: retry once if unauthorized (adjust getToken refresh logic if available)
    if (result.error && result.error.status === 401 && token) {
      // If you have a refresh flow, invoke it here then retry.
      // const refreshed = await getRefreshedToken();
      // if (refreshed) { headers.set('Authorization', `Bearer ${refreshed}`); return rawBaseQuery(args, api, extraOptions); }
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data?: T
  error?: string
}





export const arcnsliceapi = createApi({
  reducerPath: 'arcnsliceApi',
  baseQuery: authBaseQuery,
  tagTypes: ['finance'],
  endpoints: (builder) => ({

   
  getarcndatalist: builder.query<ApiResponse<ArcnList>, { showID: number; loggedInUserId: number }>({
      query: ({ showID: ShowID, loggedInUserId }) => ({
        url: `ArCnFinanceReport/GetARCNDataList/${ShowID}/${loggedInUserId || 0}`,
        method: 'POST',
      }),
      providesTags: ['finance'],
    }),
//ArCnFinanceReport/GetCustomerInvoiceDetailsByWoid

getarinvoicedetailsbywoid: builder.query<ApiResponse<ArInvoiceEntity>, number>({
      query: (WoId) => ({
        url: `ArCnFinanceReport/GetCustomerInvoiceDetailsByWoid/${WoId}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),

getarinvoicedetailsbyinvoiceid: builder.query<ApiResponse<ArInvoiceEntity>, number>({
      query: (Invoiceid ) => ({
        url: `ArCnFinanceReport/GetCustomerInvoiceDetailsByInvoiceid/${Invoiceid }`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),

    getinvoiceapproveruserlistddl: builder.query<ApiResponse<ArInvoiceEntity>, string>({
      query: (wonumber) => ({
        url: `ArCnFinanceReport/InvoiceApproverUserDll/${wonumber}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),
    getarinvoicecharge: builder.query<ApiResponse<WOSChargeLineEntity>, { invoiceid: number; WOID: number }>({
      query: ({ invoiceid, WOID }) => ({
        url: `WosChargeLine/GetArInvoiceChargeLine/${invoiceid}/${WOID}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),


    getchargecodebyuserid: builder.query<ApiResponse<any>, number>({
      query: (Userid) => ({
        url: `WosChargeLine/GetChargeCodeByUserid/${Userid}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),

    getvattaxbyuserid: builder.query<ApiResponse<any>, number>({
      query: (Userid) => ({
        url: `WosChargeLine/GetVattaxByUserid/${Userid}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),

    getservicetypesbywoid: builder.query<ApiResponse<any>, number>({
      query: (WOID) => ({
        url: `WosChargeLine/GetServicesTypesbyWOID/${WOID}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),

  createchargelinefromarinvoice: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
    query: (addchargeline) => ({
      url: 'WosChargeLine/CreateChargeLineFromARInvoice',
      method: 'POST',
      body: addchargeline,
    }),
    invalidatesTags: ['finance'],
  }),
  updateinvoicechargeline: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
    query: (updatechargeline) => ({
      url: 'WosChargeLine/UpdateInvoiceChargeline',
      method: 'PUT',
      body: updatechargeline,
    }),
    invalidatesTags: ['finance'],
  }),

  deleteinvoicechargeline: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
    query: (deletechargeline) => ({
      url: 'WosChargeLine/DeleteInvoiceChargeLine',
      method: 'PUT',
      body: deletechargeline,
    }),
    invalidatesTags: ['finance'],
  }),

  rowupinvoicechargeline: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
    query: (rowupchargeline) => ({
      url: 'WosChargeLine/RowUpInvoiceChargeline',
      method: 'PUT',
      body: rowupchargeline,
    }),
    invalidatesTags: ['finance'],
  }),

   rowdowninvoicechargeline: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
    query: (rowdownchargeline) => ({
      url: 'WosChargeLine/RowDownInvoiceChargeline',
      method: 'PUT',
      body: rowdownchargeline,
    }),
    invalidatesTags: ['finance'],
  }),



  savearinvoice: builder.mutation<ApiResponse<ArInvoiceEntity>, ArInvoiceEntity>({
    query: (savearinvoice) => ({
      url: 'ArCnFinanceReport/SaveArInvoice',
      method: 'POST',
      body: savearinvoice,
    }),
    invalidatesTags: ['finance'],
  }),


  submittedarinvwithoutdraft: builder.mutation<ApiResponse<any>, ArInvoiceEntity>({
    query: (savearinvoice) => ({
      url: 'ArCnFinanceReport/SubmittedArInvwithoutdraft',
      method: 'POST',
      body: savearinvoice,
    }),
    invalidatesTags: ['finance'],
  }),

  submittedarinvoice: builder.mutation<ApiResponse<any>, ArInvoiceEntity>({
    query: (submittedarinv) => ({
      url: 'ArCnFinanceReport/SubmittedArInvoice',
      method: 'PUT',
      body: submittedarinv,
    }),
    invalidatesTags: ['finance'],
  }),


  approvearinvoice: builder.mutation<ApiResponse<any>, ArInvoiceEntity>({
    query: (approveentity) => ({
      url: 'ArCnFinanceReport/ApproveArInvoice',
      method: 'PUT',
      body: approveentity,
    }),
    invalidatesTags: ['finance'],
  }),
  
  rejectarinvoice: builder.mutation<ApiResponse<any>, ArInvoiceEntity>({
    query: (rejectinv) => ({
      url: 'ArCnFinanceReport/RejectArInvoice',
      method: 'PUT',
      body: rejectinv,
    }),
    invalidatesTags: ['finance'],
  }),
  cancelarinvoice: builder.mutation<ApiResponse<any>, ArInvoiceEntity>({
    query: (cancelarinv) => ({
      url: 'ArCnFinanceReport/CancelArInvoice',
      method: 'PUT',
      body: cancelarinv,
    }),
    invalidatesTags: ['finance'],
  }),

  getwosalesinvoiceaudit: builder.query<ApiResponse<any>, number>({
    query: (Invoiceid) => ({
      url: `ArCnFinanceReport/GetWOSalesInvoiceAudit/${Invoiceid}`,
      method: 'GET',
    }),
    providesTags: ['finance'],
  }),

  getarinvoicedetailsbyrefinvoiceid: builder.query<ApiResponse<ArInvoiceEntity>, { WoId: number; refinvoiceid: number }>({
    query: ({ WoId, refinvoiceid }) => ({
      url: `ArCnFinanceReport/GetArCnInvoiceDetailsbyrefinvoiceid/${WoId}/${refinvoiceid}`,
      method: 'GET',
    }),
    providesTags: ['finance'],
  }),

  getarcninvoicedetailsbycnid: builder.query<ApiResponse<ArInvoiceEntity>, number>({
    query: (cnid) => ({
      url: `ArCnFinanceReport/GetArCnInvoicedetailsByCnid/${cnid}`,
      method: 'GET',
    }),
    providesTags: ['finance'],
  }),


  getarinvoicesummery: builder.query<ApiResponse<ARInvoiceSummary>, number>({
    query: (Invoiceid) => ({
      url: `ArCnFinanceReport/GetARInvoiceSummery/${Invoiceid}`,
      method: 'GET',
    }),
    providesTags: ['finance'],
  }),

  getarcnchargelines: builder.query<ApiResponse<WOSChargeLineEntity>, { invoiceid: number; CNID: number }>({
    query: ({ invoiceid, CNID }) => ({
      url: `WosChargeLine/GetArCnChargeLines/${invoiceid}/${CNID}`,
      method: 'GET',
    }),
    providesTags: ['finance'],
  }),
  copyarchargelinetoarcn: builder.mutation<ApiResponse<arcncopychargeline>, arcncopychargeline>({
    query: (addchargeline) => ({
      url: 'WosChargeLine/CopyARChargeLinesToARCN',
      method: 'POST',
      body: addchargeline,
    }),
    invalidatesTags: ['finance'],
  }),

  savearcninvoice: builder.mutation<ApiResponse<ArCreditNote>, ArCreditNote>({
    query: (savearcninvoice) => ({
      url: 'ArCnFinanceReport/SaveArCnInvoice',
      method: 'POST',
      body: savearcninvoice,
    }),
    invalidatesTags: ['finance'],
  }),

  updatearcninvoice: builder.mutation<ApiResponse<ArCreditNote>, ArCreditNote>({
    query: (updatearcninvoice) => ({
      url: 'ArCnFinanceReport/UpdateARCNInvoice',
      method: 'PUT',
      body: updatearcninvoice,
    }),
    invalidatesTags: ['finance'],
  }),


  updatearcninvoicestatus: builder.mutation<ApiResponse<ArCreditNote>, ArCreditNote>({
    query: (updatearcninvoicestatus) => ({
      url: 'ArCnFinanceReport/UpdateARCNStatus',
      method: 'PUT',
      body: updatearcninvoicestatus,
    }),
    invalidatesTags: ['finance'],
  }),

  updatewoschargelinescreditnote: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
    query: (updatechargeline) => ({
      url: 'WosChargeLine/UpdateWOSChargeLinesCreditNote',
      method: 'PUT',
      body: updatechargeline,
    }),
    invalidatesTags: ['finance'],
  }),

  deletewoschargelinescreditnote: builder.mutation<ApiResponse<WOSChargeLineEntity>, number>({
    query: (sno) => ({
      url: `WosChargeLine/DeleteWOSChargeLinesCreditNote/${sno}`,
      method: 'PUT',
    }),
    invalidatesTags: ['finance'],
  }),

  rowupcninvoicechargeline: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
    query: (rowupchargeline) => ({
      url: 'WosChargeLine/RowUpCNInvoiceChargeline',
      method: 'PUT',
      body: rowupchargeline,
    }),
    invalidatesTags: ['finance'],
  }),

  rowdowncninvoicechargeline: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
    query: (rowdownchargeline) => ({
      url: 'WosChargeLine/RowDownCNInvoiceChargeline',
      method: 'PUT',
      body: rowdownchargeline,
    }),
    invalidatesTags: ['finance'],
  }),

  getarcreditnoteaudit: builder.query<ApiResponse<any>, number>({
    query: (Invoiceid) => ({
      url: `ArCnFinanceReport/GetARCreditNoteAudit/${Invoiceid}`,
      method: 'GET',
    }),
    providesTags: ['finance'],
  }),
  getarinvoicedetailsbywoidforbluebar: builder.query<ApiResponse<ArInvoiceEntity>, number>({
      query: (WoId) => ({
        url: `ArCnFinanceReport/GetARInvoicesbyWOIDForBluebar/${WoId}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),
    getARCNByinvoiceIdForBluebar: builder.query<ApiResponse<ArInvoiceEntity>, number>({
      query: (WoId) => ({
        url: `ArCnFinanceReport/GetARCNByinvoiceIdForBluebar/${WoId}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),
  getAPInvoicesbyWOIDForBluebar: builder.query<ApiResponse<ArInvoiceEntity>, number>({
      query: (WoId) => ({
        url: `ArCnFinanceReport/GetAPInvoicesbyWOIDForBluebar/${WoId}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),
     getAPCNByinvoiceIdForBluebar: builder.query<ApiResponse<ArInvoiceEntity>, number>({
      query: (WoId) => ({
        url: `ArCnFinanceReport/GetAPCNByinvoiceIdForBluebar/${WoId}`,
        method: 'GET',
      }),
      providesTags: ['finance'],
    }),
    
    getTaxCode: builder.query<ApiResponse<string>, GetTaxCodeRequest>({
      query: (params) => ({
        url: `ArCnFinanceReport/GetTaxCode`,
        method: 'POST',
        body: params, 
      }),
      providesTags: ['finance'],
    }),
     
  }),
  
});





export const {

 
    useGetarcndatalistQuery,
    useGetarinvoicedetailsbywoidQuery,
    useGetarinvoicedetailsbyinvoiceidQuery,
    useGetinvoiceapproveruserlistddlQuery,
    useGetarinvoicechargeQuery,
    useGetchargecodebyuseridQuery,
    useGetvattaxbyuseridQuery,
    useGetservicetypesbywoidQuery,
    useCreatechargelinefromarinvoiceMutation,
    useUpdateinvoicechargelineMutation,
    useRowupinvoicechargelineMutation,
    useRowdowninvoicechargelineMutation,
    useDeleteinvoicechargelineMutation,
    useSavearinvoiceMutation,
    useSubmittedarinvwithoutdraftMutation,
    useSubmittedarinvoiceMutation,
    useApprovearinvoiceMutation,
    useRejectarinvoiceMutation,
    useCancelarinvoiceMutation,
    useGetwosalesinvoiceauditQuery,
    useGetarinvoicedetailsbyrefinvoiceidQuery,
    useGetarcninvoicedetailsbycnidQuery,
    useGetarinvoicesummeryQuery,
    useGetarcnchargelinesQuery,
    useCopyarchargelinetoarcnMutation,
    useSavearcninvoiceMutation,
    useUpdatearcninvoiceMutation,
    useUpdatearcninvoicestatusMutation,
    useUpdatewoschargelinescreditnoteMutation,
    useDeletewoschargelinescreditnoteMutation,
    useRowupcninvoicechargelineMutation,
    useRowdowncninvoicechargelineMutation,
    useGetarcreditnoteauditQuery,
    useGetarinvoicedetailsbywoidforbluebarQuery,
    useGetARCNByinvoiceIdForBluebarQuery,
    useGetAPInvoicesbyWOIDForBluebarQuery,
    useGetAPCNByinvoiceIdForBluebarQuery,
    useGetTaxCodeQuery,


} = arcnsliceapi;
